import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    as?: 'h1' | 'h2'
    id?: string
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'left',
    tone = 'light',
    as: Heading = 'h2',
    id,
    className
}): ReactNode => {
    const isDark = tone === 'dark'

    return (
        <div
            className={cn(
                'max-w-3xl',
                align === 'center' && 'mx-auto text-center',
                className
            )}
        >
            {eyebrow && (
                <p
                    className={cn(
                        'text-xs font-bold tracking-[0.2em] uppercase',
                        isDark ? 'text-gold' : 'text-bronze'
                    )}
                >
                    {eyebrow}
                </p>
            )}

            <Heading
                id={id}
                className={cn(
                    'font-heading font-medium tracking-tight',
                    Heading === 'h1'
                        ? 'text-4xl leading-[1.08] sm:text-5xl lg:text-6xl'
                        : 'text-3xl leading-[1.12] sm:text-4xl lg:text-5xl',
                    eyebrow && 'mt-4',
                    isDark ? 'text-primary-foreground' : 'text-foreground'
                )}
            >
                {title}
            </Heading>

            {description && (
                <p
                    className={cn(
                        'mt-5 text-base leading-relaxed sm:text-lg',
                        isDark ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    )}
                >
                    {description}
                </p>
            )}
        </div>
    )
}
